/**
 * Swap Test UI Page
 * Uniswap-style swap interface for token swapping
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { ExchangeClient } from "../sdk";
import { useOnboard } from "../hooks/useOnboard";
import { DEFAULT_BASE_URL } from "../configs/base";
import "./swap_test_page/swap_test.css";

interface Token {
  symbol: string;
  name: string;
  address: string;
  decimals: number;
  logoURI?: string;
}

// Common tokens list (can be expanded)
const COMMON_TOKENS: Token[] = [
  {
    symbol: "ETH",
    name: "Ethereum",
    address: "ETH",
    decimals: 18,
  },
  {
    symbol: "BTC",
    name: "Bitcoin",
    address: "BTC",
    decimals: 8,
  },
  {
    symbol: "USDC",
    name: "USD Coin",
    address: "USDC",
    decimals: 6,
  },
  {
    symbol: "USDT",
    name: "Tether",
    address: "USDT",
    decimals: 6,
  },
  {
    symbol: "DAI",
    name: "Dai Stablecoin",
    address: "DAI",
    decimals: 18,
  },
];

export default function SwapTestUI() {
  const [baseURL, setBaseURL] = useState(DEFAULT_BASE_URL);
  const [fromToken, setFromToken] = useState<Token | null>(COMMON_TOKENS[0]);
  const [toToken, setToToken] = useState<Token | null>(COMMON_TOKENS[2]);
  const [fromAmount, setFromAmount] = useState<string>("");
  const [toAmount, setToAmount] = useState<string>("");
  const [isFromTokenSelectorOpen, setIsFromTokenSelectorOpen] = useState(false);
  const [isToTokenSelectorOpen, setIsToTokenSelectorOpen] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [slippage, setSlippage] = useState<number>(0.5); // 0.5% default slippage
  const [priceImpact, setPriceImpact] = useState<number | null>(null);
  const [exchangeRate, setExchangeRate] = useState<string>("");

  const exchangeClientRef = useRef<ExchangeClient | null>(null);
  const baseURLRef = useRef<string>(baseURL);

  // Use onboarding hook
  const {
    mnemonic,
    walletAddress,
    setMnemonic: setOnboardMnemonic,
  } = useOnboard(baseURL);

  // Update refs
  useEffect(() => {
    baseURLRef.current = baseURL;
  }, [baseURL]);

  // Initialize ExchangeClient
  useEffect(() => {
    if (mnemonic && mnemonic.trim() !== "") {
      exchangeClientRef.current = new ExchangeClient({
        baseURL: baseURLRef.current,
        mnemonic,
      });
      exchangeClientRef.current.initialize().catch((error) => {
        console.error("Failed to initialize ExchangeClient:", error);
      });
    }
  }, [mnemonic, baseURL]);

  // Calculate exchange rate and price impact (mock calculation for demo)
  useEffect(() => {
    if (fromAmount && parseFloat(fromAmount) > 0 && fromToken && toToken) {
      // Mock exchange rate calculation (1:1 for demo, replace with actual API call)
      const mockRate = 1; // This should be fetched from API
      const calculatedToAmount = parseFloat(fromAmount) * mockRate;
      setToAmount(calculatedToAmount.toFixed(6));
      setExchangeRate(`1 ${fromToken.symbol} = ${mockRate.toFixed(4)} ${toToken.symbol}`);
      // Mock price impact (should be calculated based on liquidity)
      setPriceImpact(0.1); // 0.1% impact
    } else {
      setToAmount("");
      setExchangeRate("");
      setPriceImpact(null);
    }
  }, [fromAmount, fromToken, toToken]);

  // Handle swap tokens
  const handleSwapTokens = useCallback(() => {
    const temp = fromToken;
    setFromToken(toToken);
    setToToken(temp);
    const tempAmount = fromAmount;
    setFromAmount(toAmount);
    setToAmount(tempAmount);
  }, [fromToken, toToken, fromAmount, toAmount]);

  // Handle swap execution
  const handleSwap = useCallback(async () => {
    if (!exchangeClientRef.current || !fromToken || !toToken || !fromAmount) {
      toast.error("Setup required", {
        description: "Please ensure mnemonic and swap details are configured.",
      });
      return;
    }

    if (parseFloat(fromAmount) <= 0) {
      toast.error("Invalid amount", {
        description: "Please enter a valid amount to swap.",
      });
      return;
    }

    if (!walletAddress) {
      toast.error("Wallet required", {
        description: "Please connect your wallet first.",
      });
      return;
    }

    setIsSwapping(true);

    try {
      const amountIn = parseFloat(fromAmount);
      // Calculate minimum amount out with slippage tolerance
      const estimatedAmountOut = parseFloat(toAmount);
      const minAmountOut = estimatedAmountOut * (1 - slippage / 100);

      const toastId = toast.info("Swapping tokens", {
        description: `Swapping ${fromAmount} ${fromToken.symbol} for ${toToken.symbol}...`,
      });

      await exchangeClientRef.current.swap({
        fromToken: fromToken.address,
        toToken: toToken.address,
        amountIn,
        minAmountOut,
      });

      toast.dismiss(toastId);
      toast.success("Swap successful", {
        description: `Successfully swapped ${fromAmount} ${fromToken.symbol} for ${toToken.symbol}`,
      });

      // Reset amounts after successful swap
      setFromAmount("");
      setToAmount("");
    } catch (error: any) {
      toast.error("Swap failed", {
        description: error.message || "Unknown error occurred",
      });
    } finally {
      setIsSwapping(false);
    }
  }, [fromToken, toToken, fromAmount, toAmount, slippage, walletAddress]);

  // Validate swap button state
  const canSwap = useMemo(() => {
    return (
      !!fromToken &&
      !!toToken &&
      !!fromAmount &&
      parseFloat(fromAmount) > 0 &&
      !!walletAddress &&
      !isSwapping &&
      fromToken.symbol !== toToken.symbol
    );
  }, [fromToken, toToken, fromAmount, walletAddress, isSwapping]);

  // Get token icon (simple emoji-based for demo)
  const getTokenIcon = (symbol: string): string => {
    const icons: { [key: string]: string } = {
      ETH: "⟠",
      BTC: "₿",
      USDC: "$",
      USDT: "$",
      DAI: "◈",
    };
    return icons[symbol] || "●";
  };

  return (
    <div className="App-content-row">
      <div className="swap-test-header">
        <h1>Swap</h1>
        <p className="swap-test-subtitle">Swap anytime, anywhere</p>
      </div>

      {/* Configuration Section */}
      <div className="App-section swap-config-section">
        <h3>Configuration</h3>
        <div className="swap-config-grid">
          <div>
            <label>
              HTTP Base URL:
              <input
                type="text"
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
                className="swap-input"
              />
            </label>
          </div>
          <div>
            <label>
              Mnemonic:
              <input
                type="text"
                value={mnemonic || ""}
                onChange={(e) => setOnboardMnemonic(e.target.value)}
                placeholder="Enter mnemonic"
                className="swap-input"
              />
            </label>
          </div>
          <div>
            <label>
              Wallet Address:
              <input
                type="text"
                value={walletAddress || ""}
                disabled
                placeholder="Auto-generated from mnemonic"
                className="swap-input"
              />
            </label>
          </div>
          <div>
            <label>
              Slippage Tolerance (%):
              <input
                type="number"
                value={slippage}
                onChange={(e) => setSlippage(parseFloat(e.target.value) || 0.5)}
                min="0"
                max="50"
                step="0.1"
                className="swap-input"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Swap Interface */}
      <div className="swap-container">
        <div className="swap-card">
          {/* From Token Section */}
          <div className="swap-token-section">
            <div className="swap-token-label">Sell</div>
            <div className="swap-token-input-group">
              <input
                type="number"
                className="swap-amount-input"
                placeholder="0"
                value={fromAmount}
                onChange={(e) => setFromAmount(e.target.value)}
                min="0"
                step="any"
              />
              <div className="swap-token-selector-wrapper">
                <button
                  className="swap-token-selector"
                  onClick={() => {
                    setIsFromTokenSelectorOpen(!isFromTokenSelectorOpen);
                    setIsToTokenSelectorOpen(false);
                  }}
                >
                  {fromToken ? (
                    <>
                      <span className="swap-token-icon">
                        {getTokenIcon(fromToken.symbol)}
                      </span>
                      <span className="swap-token-symbol">{fromToken.symbol}</span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 9L1 4H11L6 9Z"
                          fill="currentColor"
                        />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>Select token</span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 9L1 4H11L6 9Z"
                          fill="currentColor"
                        />
                      </svg>
                    </>
                  )}
                </button>
                {isFromTokenSelectorOpen && (
                  <div className="swap-token-dropdown">
                    {COMMON_TOKENS.map((token) => (
                      <button
                        key={token.address}
                        className="swap-token-option"
                        onClick={() => {
                          if (token.address === toToken?.address) {
                            handleSwapTokens();
                          } else {
                            setFromToken(token);
                            setIsFromTokenSelectorOpen(false);
                          }
                        }}
                      >
                        <span className="swap-token-icon">
                          {getTokenIcon(token.symbol)}
                        </span>
                        <div className="swap-token-option-info">
                          <span className="swap-token-option-symbol">
                            {token.symbol}
                          </span>
                          <span className="swap-token-option-name">
                            {token.name}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {fromAmount && parseFloat(fromAmount) > 0 && (
              <div className="swap-token-value">
                ${(parseFloat(fromAmount) * 1).toFixed(2)}
              </div>
            )}
          </div>

          {/* Swap Button */}
          <div className="swap-button-container">
            <button
              className="swap-button-middle"
              onClick={handleSwapTokens}
              aria-label="Swap tokens"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 6L8 2L12 6M12 10L8 14L4 10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* To Token Section */}
          <div className="swap-token-section">
            <div className="swap-token-label">Buy</div>
            <div className="swap-token-input-group">
              <input
                type="number"
                className="swap-amount-input"
                placeholder="0"
                value={toAmount}
                onChange={(e) => setToAmount(e.target.value)}
                min="0"
                step="any"
                readOnly
              />
              <div className="swap-token-selector-wrapper">
                <button
                  className="swap-token-selector"
                  onClick={() => {
                    setIsToTokenSelectorOpen(!isToTokenSelectorOpen);
                    setIsFromTokenSelectorOpen(false);
                  }}
                >
                  {toToken ? (
                    <>
                      <span className="swap-token-icon">
                        {getTokenIcon(toToken.symbol)}
                      </span>
                      <span className="swap-token-symbol">{toToken.symbol}</span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 9L1 4H11L6 9Z"
                          fill="currentColor"
                        />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>Select token</span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 9L1 4H11L6 9Z"
                          fill="currentColor"
                        />
                      </svg>
                    </>
                  )}
                </button>
                {isToTokenSelectorOpen && (
                  <div className="swap-token-dropdown">
                    {COMMON_TOKENS.map((token) => (
                      <button
                        key={token.address}
                        className="swap-token-option"
                        onClick={() => {
                          if (token.address === fromToken?.address) {
                            handleSwapTokens();
                          } else {
                            setToToken(token);
                            setIsToTokenSelectorOpen(false);
                          }
                        }}
                      >
                        <span className="swap-token-icon">
                          {getTokenIcon(token.symbol)}
                        </span>
                        <div className="swap-token-option-info">
                          <span className="swap-token-option-symbol">
                            {token.symbol}
                          </span>
                          <span className="swap-token-option-name">
                            {token.name}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {toAmount && parseFloat(toAmount) > 0 && (
              <div className="swap-token-value">
                ${(parseFloat(toAmount) * 1).toFixed(2)}
              </div>
            )}
          </div>

          {/* Swap Info */}
          {exchangeRate && (
            <div className="swap-info">
              <div className="swap-info-row">
                <span>Exchange Rate</span>
                <span>{exchangeRate}</span>
              </div>
              {priceImpact !== null && (
                <div className="swap-info-row">
                  <span>Price Impact</span>
                  <span className={priceImpact > 1 ? "swap-warning" : ""}>
                    {priceImpact.toFixed(2)}%
                  </span>
                </div>
              )}
              <div className="swap-info-row">
                <span>Slippage Tolerance</span>
                <span>{slippage}%</span>
              </div>
            </div>
          )}

          {/* Swap Button */}
          <button
            className="swap-button-primary"
            onClick={handleSwap}
            disabled={!canSwap}
          >
            {!walletAddress
              ? "Connect Wallet"
              : isSwapping
                ? "Swapping..."
                : fromToken?.symbol === toToken?.symbol
                  ? "Select different tokens"
                  : !fromAmount || parseFloat(fromAmount) <= 0
                    ? "Enter amount"
                    : "Swap"}
          </button>
        </div>

        {/* Footer Info */}
        <div className="swap-footer">
          <p>Buy and sell crypto on 16+ networks including</p>
        </div>
      </div>
    </div>
  );
}

